import { bsc, pulsechain } from "viem/chains";

import {
  field,
  getPublicClient,
  readContract,
  toBool,
  writeContractAndWait,
} from "../viem";

import MafiaInventoryABI from "../../abis/MafiaInventory.json";
import MafiaInventoryPlsABI from "../../abis/MafiaInventoryPLS.json";

import config, { CHAIN } from "../../config/config";
import { ChainType, ItemCategory } from "../../constants/enum/enum";
import { Category } from "../../types/Contract/Inventory/Category";
import { UserNonceStatus } from "../../types/Contract/Inventory/UserNonceStatus";

const address = config.inventoryAddress;
const abi =
  CHAIN === ChainType.BNB ? MafiaInventoryABI : MafiaInventoryPlsABI;

export const INVENTORY_CHUNK_SIZE_BY_CHAIN: Record<ChainType, number> = {
  [ChainType.BNB]: 100,
  [ChainType.PLS]: 50,
};

const MULTICALL_BATCH_SIZE = 25;
const multicallAddress = CHAIN === ChainType.BNB
  ? bsc.contracts.multicall3.address
  : pulsechain.contracts.multicall3.address;

export interface ContractInventoryItem {
  itemId: number;
  categoryId: number;
  typeId: number;
  owner: string;
  cityId: number;
  damagePercent?: number;
}

const parseContractItems = (result: any): ContractInventoryItem[] => {
  const itemIds = field(result, "itemIds", 0) || [];
  const list = field(result, "list", 1) || [];
  const cities = field(result, "cities", 2) || [];

  return itemIds.map((itemId: unknown, index: number) => ({
    itemId: Number(itemId),
    categoryId: Number(field(list[index], "categoryId", 0)),
    typeId: Number(field(list[index], "typeId", 1)),
    owner: String(field(list[index], "owner", 2)),
    cityId: Number(cities[index]),
  }));
};

const getPagedInventoryItems = async ({
  makeArgs,
  startIndex: inputStartIndex = 0,
  maxItems = Number.POSITIVE_INFINITY,
}: {
  makeArgs: (startIndex: number, chunkSize: number) => readonly unknown[];
  startIndex?: number;
  maxItems?: number;
}): Promise<ContractInventoryItem[]> => {
  const client = getPublicClient();
  const chunkSize = INVENTORY_CHUNK_SIZE_BY_CHAIN[CHAIN];
  const normalizedMaxItems = Number.isFinite(maxItems)
    ? Math.max(0, Math.floor(maxItems))
    : Number.POSITIVE_INFINITY;

  const allItems: ContractInventoryItem[] = [];
  let startIndex = Math.max(0, Math.floor(inputStartIndex));
  let reachedEnd = normalizedMaxItems === 0;

  while (!reachedEnd && allItems.length < normalizedMaxItems) {
    const remainingItems = normalizedMaxItems - allItems.length;
    const batchSize = Math.min(
      MULTICALL_BATCH_SIZE,
      Math.ceil(remainingItems / chunkSize)
    );
    const contracts = Array.from({ length: batchSize }, (_, index) => ({
      address: address as `0x${string}`,
      abi,
      functionName: "getItemsByCategory" as const,
      args: makeArgs(startIndex + index * chunkSize, chunkSize),
    }));

    const results = await client.multicall({
      contracts: contracts as any,
      allowFailure: false,
      multicallAddress,
    });

    for (const result of results) {
      const page = parseContractItems(result);
      if (page.length === 0) {
        reachedEnd = true;
        break;
      }

      allItems.push(...page);
      if (page.length < chunkSize) {
        reachedEnd = true;
        break;
      }
    }

    startIndex += batchSize * chunkSize;
  }

  const seenItemIds = new Set<number>();
  return allItems
    .filter((item) => {
      if (seenItemIds.has(item.itemId)) return false;
      seenItemIds.add(item.itemId);
      return true;
    })
    .slice(0, normalizedMaxItems);
};

export const getItemsByCategory = async (
  categoryId: number,
  startIndex = 0,
  maxItems = Number.POSITIVE_INFINITY
) => getPagedInventoryItems({
  makeArgs: (pageStartIndex, chunkSize) => [
    BigInt(categoryId),
    BigInt(pageStartIndex),
    BigInt(chunkSize),
  ],
  startIndex,
  maxItems,
});

export const getCarItemsByOwner = async (
  owner: string,
  startIndex = 0,
  maxItems = Number.POSITIVE_INFINITY
) => {
  const cars = (await getItemsByCategory(ItemCategory.CAR_ITEM, startIndex, maxItems))
    .filter((item) => item.owner.toLowerCase() === owner.toLowerCase());
  if (cars.length === 0) return cars;

  const client = getPublicClient();
  const damagePercents: number[] = [];
  for (let start = 0; start < cars.length; start += MULTICALL_BATCH_SIZE) {
    const results = await client.multicall({
      contracts: cars.slice(start, start + MULTICALL_BATCH_SIZE).map((car) => ({
        address: address as `0x${string}`,
        abi,
        functionName: "carDamagePercent" as const,
        args: [BigInt(car.itemId)],
      })) as any,
      allowFailure: false,
      multicallAddress,
    });
    damagePercents.push(...results.map(Number));
  }

  return cars.map((car, index) => ({
    ...car,
    damagePercent: damagePercents[index],
  }));
};

export const getNonceStatus = async (account: string) => {
  const fulfilled = await readContract({
    address,
    abi,
    functionName: "getNonceStatus",
    args: [account],
    useWallet: true,
  });

  return toBool(fulfilled);
};

export const getUserStatus = async (account: string) => {
  const userStatus: UserNonceStatus = await readContract({
    address,
    abi,
    functionName: "userNonceStatus",
    args: [account],
    useWallet: true,
  });

  return {
    isPending: toBool(field(userStatus, "isPending", 0)),
    requestId: Number(field(userStatus, "requestId", 1) ?? 0),
  } as UserNonceStatus;
};

export const getStockCounts = async () => {
  const categories: Category[] = await readContract({
    address,
    abi,
    functionName: "getCategories",
  });

  return {
    categoryStocks: categories.map((category: Category) => {
      return Number(category.totalStock);
    }),

    itemStocks: categories.map((category: Category) => {
      return category.types.map((type) => Number(type.stock));
    }),
  };
};

export const requestOpenCrate = async (account: string) => {
  await writeContractAndWait({
    address,
    abi,
    functionName: "requestOpenCrate",
    account,
  });
};

export const finishOpenCrate = async (account: string) => {
  await writeContractAndWait({
    address,
    abi,
    functionName: "finishOpenCrate",
    account,
  });
};

export const transferItem = async (
  account: string,
  to: string,
  itemId: number
) => {
  await writeContractAndWait({
    address,
    abi,
    functionName: "transferItem",
    args: [to, itemId],
    account,
  });
};

export const transferItems = async (
  account: string,
  to: string,
  itemIds: number[]
) => {
  await writeContractAndWait({
    address,
    abi,
    functionName: "transferItems",
    args: [to, itemIds],
    account,
  });
};

export const addItems = async (
  account: string,
  recipient: `0x${string}`,
  categoryIds: bigint[],
  typeIds: bigint[]
) => {
  if (
    categoryIds.length === 0 ||
    categoryIds.length !== typeIds.length
  ) {
    throw new Error("Item arrays must be non-empty and have equal lengths.");
  }

  return writeContractAndWait({
    address,
    abi,
    functionName: "addItems",
    args: [recipient, categoryIds, typeIds],
    account,
  });
};

export const getLatestAirdropTimestamp = async () => {
  const latestAirdropTimestamp = await readContract({
    address,
    abi,
    functionName: "latestAirdropTimestamp",
  });

  return Number(latestAirdropTimestamp);
};

export const getCategoryAvailable = async (categoryId: number) => {
  const isAvailable: boolean = await readContract({
    address,
    abi,
    functionName: "isCategoryAvailable",
    args: [categoryId],
  });

  return isAvailable;
};

export const wrapOGCrate = async (account: string) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "wrapOGCrate",
    account,
  });

  return data;
};

export const unwrapOGCrate = async (account: string, itemId: number) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "unwrapOGCrate",
    args: [itemId],
    account,
  });

  return data;
};

export const wrapOGNFT = async (account: string) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "wrapOGNFT",
    account,
  });

  return data;
};

export const unwrapOGNFT = async (account: string, itemId: number) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "unwrapOGNFT",
    args: [itemId],
    account,
  });

  return data;
};

export const unwrapKeyItem = async (account: string, itemId: number) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "unwrapKeyItem",
    args: [itemId],
    account,
  });

  return data;
};

export const unwrapMafiaItem = async (account: string, itemId: number) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "unwrapMafiaItem",
    args: [itemId],
    account,
  });

  return data;
};

export const unwrapOGNFTItem = async (account: string, itemId: number) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "unwrapOGNFTItem",
    args: [itemId],
    account,
  });

  return data;
};

export const unwrapBulletItems = async (account: string, itemIds: number[]) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "unwrapBulletItems",
    args: [itemIds],
    account,
  });

  return data;
};

export const unwrapHealthItems = async (account: string, itemIds: number[]) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "unwrapHealthItems",
    args: [itemIds],
    account,
  });

  return data;
};

export const shipCars = async (
  account: string,
  itemIds: number[],
  destinationCityId: number
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "shipCars",
    args: [itemIds, itemIds.map(() => destinationCityId)],
    account,
  });

  return data;
};

export const repairCars = async (account: string, itemIds: number[]) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "repairCars",
    args: [itemIds],
    account,
  });

  return data;
};

export const sellCars = async (account: string, itemIds: number[]) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "sellCars",
    args: [itemIds],
    account,
  });
  return data;
};

export const mintNewCars = async (
  account: string,
  itemIds: number[],
  signMsg: string,
  signature: string
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "mintNewCars",
    args: [itemIds, JSON.stringify(signMsg), signature],
    account,
  });

  return data;
};

export const unwrapCreditsItem = async (account: string, itemIds: number[]) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "unwrapCreditsItem",
    args: [itemIds],
    account,
  });

  return data;
};

export const divideHealthItems = async (account: string, itemIds: number[]) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "divideHealthItems",
    args: [itemIds],
    account,
  });

  return data;
};
